import React, { useState } from "react";
import cx from "classnames";
import styles from "./PatientSelector.module.scss";
import { Button, ButtonRole } from "components/Button";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { InfoErrorTooltip } from "components/InfoTooltip";
import profileIcon from "images/profile-dummy.svg";
import xcircle from "images/ciamXcircle.svg";
import info from "images/ciamInfo.svg";

interface PatientCardProps {
  patient: any;
  index: number;
  masked: boolean;
  idToken: string;
  showConfirmForm?: (masked: boolean, patient: any, idToken: string, index: number) => void;
  errorContainer?: boolean;
  calledFrom?: string;
}

export const PatientContainer: React.FC<PatientCardProps> = ({
  patient,
  index,
  masked,
  idToken,
  showConfirmForm,
  errorContainer,
  calledFrom
}) => {
  let phoneNumber =
    !patient.Phones || typeof patient.Phones[0] !== "object"
      ? ""
      : patient.Phones[0]["PhoneNumber"];

  const showConfirmFormTest = () => {
    if (showConfirmForm) {
      showConfirmForm(masked, patient, idToken, index);
    }
  };

  const maskEmail = (email: string) => {
    const [user, domain] = email.split("@");
    const maskedUser = user[0] + "**********";
    const maskedDomain = domain[0] + domain[1] + domain[2] + "*******";
    return `${maskedUser}@${maskedDomain}`;
  };

  const maskName = (name: string) => {
    return name[0] + "******";
  };

  // ✅ FIXED — safe for ISO, blank, or masked values
  const maskDate = (date: string) => {
    if (!date) return "";

    // already masked
    if (date.includes("**")) return date;

    // try parsing with dayjs (ISO-safe)
    const parsed = dayjs(date);
    if (parsed.isValid()) {
      return `**/**/**${parsed.format("YYYY").slice(-2)}`;
    }

    // fallback for MM/DD/YYYY
    const parts = String(date).split("/");
    if (!parts[2]) return "**/**/****";

    return `**/**/**${parts[2].slice(-2)}`;
  };

  const maskPhone = (pNum: string) => {
    return "**" + pNum[2] + "-**" + pNum[5] + "-***" + pNum[9];
  };

  const firstName =
    calledFrom === "Question" ? maskName(patient.FirstName) : patient.FirstName;
  const lastName =
    calledFrom === "Question" ? maskName(patient.LastName) : patient.LastName;
  const email =
    calledFrom === "Question" ? maskEmail(patient.Email) : patient.Email;
  const dob =
    calledFrom === "Question" || masked
      ? maskDate(patient.Dob)
      : dayjs(patient.Dob).format("MM/DD/YYYY");

  phoneNumber =
    calledFrom === "Question" && phoneNumber
      ? maskPhone(phoneNumber)
      : phoneNumber;

  return (
    <>
      <div>
        {patient.AccountType === "Main" && (
          <h4 className={styles.accHeader}>ACCOUNT HOLDER</h4>
        )}
        <div
          className={
            errorContainer ? cx(styles.card, styles.errCard) : styles.card
          }
        >
          <div className={styles.item}>
            <div className={styles.icon}>
              <img src={profileIcon} alt="profile" />
            </div>
            <div className={styles.info} aria-hidden="false">
              <div className={styles.nameWrapper}>
                <span className={styles.patientText}>Patient </span>
                <p className={styles.patientName}>
                  {firstName} {lastName}
                </p>
              </div>
              {!errorContainer && (
                <div>
                  <Button
                    role={ButtonRole.Standard}
                    className={styles.confirmDataLink}
                    text="SELECT"
                    onClick={showConfirmFormTest}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.divider}></div>

          <div className={styles.buttons}>
            <div className={styles.row}>
              <div className={styles.field}>
                <span>Date of Birth</span>
                <p className={styles.date}>{dob}</p>
              </div>
              <div className={styles.field}>
                <span>Phone Number</span>
                <p className={styles.date}>{phoneNumber}</p>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <span>Email</span>
                <p className={styles.date}>{email}</p>
              </div>
            </div>
          </div>
        </div>

        {errorContainer && (
          <div className={styles.errContainer}>
            <div className={styles.errRow}>
              <div>
                <img src={xcircle} alt="profile" />
              </div>
              <div className={styles.errText}>
                We couldn’t confirm your identity. Please{" "}
                <a
                  href="https://locations.myeyedr.com/search"
                  target="_blank"
                >
                  contact your local MyEyeDr. office
                </a>{" "}
                for further assistance.
              </div>
              <div>
                <InfoErrorTooltip>
                  <p>
                    To access this patient's account, please verify the account
                    holder information. If you can’t confirm the patient due to
                    an error in the information provided, please contact us for
                    support.
                  </p>
                </InfoErrorTooltip>
              </div>
            </div>
          </div>
        )}
      </div>

      {index === 0 && (
        <h4 className={styles.accHeader}>PATIENTS UNDER YOUR CARE</h4>
      )}
    </>
  );
};
